import { Autocomplete, Divider, Link, ListItemText, TextField } from "@mui/material";
import React, { forwardRef, Fragment } from "react";
import { RadiusSelect } from "../SearchCard/GooglePlacesInput/RadiusSelect";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { styled } from "@mui/system";
import { SearchTypesSP } from "../SearchCard/SalesPartnerSearchBox";
import { useTranslation } from "react-i18next";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import classes from "./GroupedSelect.module.less";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import {
  C_ON_SECONDARY_CONTAINER,
  C_ON_SURFACE_LOW_EMPHASIS,
  C_OUTLINE_VARIANT,
  C_SURFACE,
} from "~/common/components/icon/Colors";
import { LinkButton } from "~/common/components/Link";
import { ELEVATION_2 } from "~/common/components/icon/Elevation";

export interface GroupedSelectRef {
  reset: () => void;
}

export interface Suggestion {
  label: string;
  group: string;
}

interface GroupedSelectProps {
  disabled?: boolean;
  openFilterDialog: () => void;
  showFilterButton: boolean;
  selectedOption: Suggestion | null;
  setSelectedOption: React.Dispatch<React.SetStateAction<Suggestion | null>>;
  suggestedCombinedList: Suggestion[];
  onSearchInputChange: (input: string) => void;
}

const GroupHeader = styled("div")({
  position: "sticky",
  padding: "8px",
  paddingTop: "16px",
  color: C_ON_SURFACE_LOW_EMPHASIS,
  backgroundColor: C_SURFACE,
  textTransform:'capitalize',
  ...getFontStyle(FontSize.BodySmall),
});

const GroupItems = styled("ul")({
  padding: "0px",
  backgroundColor: C_SURFACE,
  ...getFontStyle(FontSize.BodyLarge),
});

const groupedSelectslotProps={
  paper: {
    sx: {
      boxShadow: ELEVATION_2,
      '& ul': {
        backgroundColor: C_SURFACE,
      },
    },
  },
  popper: {
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
    ],
  },
}

const GroupedSelect = forwardRef<GroupedSelectRef, GroupedSelectProps>((props, ref) => {
  const screenSize = useNumericScreenSize();
  const isMobile = screenSize <= useNumericScreenSize.phone;

  const {
    disabled,
    openFilterDialog,
    showFilterButton,
    selectedOption,
    suggestedCombinedList,
    onSearchInputChange,
    setSelectedOption,
  } = props;

  const { t } = useTranslation();

  const autocompleteCombinedInput = (clickable: boolean) => (
    <div className={classes.groupedSelectContainer}>
      <Autocomplete
        disabled={disabled}
        fullWidth
        value={selectedOption}
        filterOptions={(x) => x} // skip MUI's default filter
        options={suggestedCombinedList}
        open={clickable ? false : undefined}
        groupBy={(option) => (typeof option === "string" ? "" : option.group)}
        getOptionLabel={(option) => (typeof option === "string" ? option : option.label)}
        renderGroup={(params) => (
          <li key={params.key}>
            <GroupHeader>{params.group}</GroupHeader>
            <GroupItems>{params.children}</GroupItems>
          </li>
        )}
        renderOption={(props, option, { index }) => (
          <Fragment key={option.label}>
            <li {...props}>
              <ListItemText primary={option.label} />
            </li>
            <Divider />
          </Fragment>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={t("partnerFinder.search.placeholder")}
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: (
                  <div className="ml-12">
                    <Icon type={IconType.Search} />
                  </div>
                ),
              },
            }}
          />
        )}
        onInputChange={(e, newValue) => {
          onSearchInputChange(newValue);
        }}
        onChange={(event, newValue) => {
          setSelectedOption(newValue);
        }}
        sx={{
          "& .MuiInputBase-root": {
            borderRadius: 0,
            borderRight: isMobile ? 0 : `1px solid ${C_OUTLINE_VARIANT}`,
            paddingRight: 0,
            height: isMobile ? "48px" : "56px"
          },
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
        }}
        slotProps={groupedSelectslotProps}
      />
      {!isMobile && (
        <RadiusSelect disabled={typeof selectedOption !== "string" && selectedOption?.group == SearchTypesSP.Partner} />
      )}
    </div>
  );

  const searchClickableButton = (
    <LinkButton onClick={openFilterDialog} className={classes.linkButton}>
      {isMobile && autocompleteCombinedInput(true)}
      <div className={classes.filterIconContainer}>
        <FilterAltOutlinedIcon
          fontSize="large"
          sx={{
            color: C_ON_SECONDARY_CONTAINER,
          }}
        />
      </div>
    </LinkButton>
  );

  return (
    <div className={classes.groupedSelectContainerWithFilterIcon}>
      {autocompleteCombinedInput(false)}
      {showFilterButton && isMobile && searchClickableButton}
    </div>
  );
});

export default GroupedSelect;
